import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import LandingPage from './LandingPage';
import EventsPage from './EventsPage';
import EventTimelinePage from './EventTimelinePage';
import GalleryPage from './GalleryPage';
import ContactPage from './ContactPage';
import AccountPage from './AccountPage';
import LoginRegister from './LoginRegister';
import DevelopersPage from './DevelopersPage';
import CoordinatorsPage from './CoordinatorsPage';
import SponsorsPage from './SponsorsPage';
import HackathonPage from './HackathonPage';
import WorkshopPage from './WorkshopPage';
import LaserLoader from './components/LaserLoader/LaserLoader';
import { AuthProvider, useAuth } from './AuthContext';
import './HeroPage.css';

function App() {
  const [loading, setLoading] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    // Check if mobile view
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", checkMobile);

    // Preload gallery images
    const galleryImages = [
      '/images/batch22-26.JPG',
      '/images/batch23-27.JPG',
      '/images/cryptera.JPG',
      '/images/cryptera2.JPG',
      '/images/IMG_7903.JPG',
      '/images/IMG_8024 (1).JPG',
      '/images/IMG_8103.JPG',
      '/images/IMG_8162.JPG',
      '/images/IMG_8327.JPG',
      '/images/IMG_8352.JPG',
      '/images/IMG_8359.JPG',
      '/images/IMG_8888.JPG',
      '/images/IMG_9108.JPG',
      '/images/IMG_9183.JPG',
      '/images/staffs.JPG',
      '/images/staffs2.JPG'
    ];

    let loadedCount = 0;
    const totalImages = galleryImages.length;
    const minLoadTime = 5000;
    const startTime = Date.now();

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minLoadTime - elapsed);
        setTimeout(() => setLoading(false), remaining);
      }
    };

    // Preload all images and wait for them to load
    galleryImages.forEach(src => {
      const img = new Image();
      img.onload = checkAllLoaded;
      img.onerror = checkAllLoaded; // Continue even if image fails
      img.src = src;
    });

    // Fallback: hide loader after 8 seconds max
    const maxTimer = setTimeout(() => setLoading(false), 8000);

    return () => {
      clearTimeout(maxTimer);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (loading) {
      setMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  return (
    <>
      {loading && <LaserLoader />}
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </>
  );
}

function AppContent() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (sectionId) => {
    setIsMenuOpen(false);

    if (sectionId === 'home' || sectionId === 'about' || sectionId === 'hackathon' || sectionId === 'workshop' || sectionId === 'sponsors' || sectionId === 'contact') {
      if (location.pathname !== '/') {
        navigate('/', { state: { targetId: sectionId } });
      } else {
        scrollToSection(sectionId);
      }
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
        const navbarHeight = document.querySelector('.navbar').offsetHeight;
        const elementPosition = element.offsetTop - navbarHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth"
        });
    }
  };

  const handleAccountClick = () => {
    navigate("/account");
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (
      location.pathname === "/" &&
      location.state &&
      location.state.targetId
    ) {
      setTimeout(() => {
        scrollToSection(location.state.targetId);
      }, 100);
    }
  }, [location]);

  // Close login modal when route changes
  useEffect(() => {
    setShowLogin(false);
  }, [location.pathname]);

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/images/logo.png" alt="Cryptera Logo" className="logo-img" />
            <h2>CRYPTERA 2K26</h2>
            <Link to="/" onClick={() => handleNavClick("home")}></Link>
          </div>
          <ul className={`nav-menu ${isMenuOpen ? "active" : ""}`}>
            <li>
              <a
                href="#home"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("home");
                }}
              >
                Home
              </a>
            </li>
            <li>
              <a
                href="#about"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("about");
                }}
              >
                About
              </a>
            </li>
            <li>
              <a
                href="#sponsors"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("sponsors");
                }}
              >
                Sponsors
              </a>
            </li>
            <li>
              <a
                href="#hackathon"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("hackathon");
                }}
              >
                Hackathon
              </a>
            </li>
            <li>
              <a
                href="#workshop"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("workshop");
                }}
              >
                Workshop
              </a>
            </li>
            <li>
              <Link
                to="/events"
                className="nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Events
              </Link>
            </li>
            <li>
              <Link
                to="/eventtimeline"
                className="nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Event Timeline
              </Link>
            </li>

            {/*<li>
              <Link
                to="/coordinators"
                className="nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                Coordinators
              </Link>
            </li>*/}
            <li>
              <a
                href="#contact"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("contact");
                }}
              >
                Contact
                </a>
            </li>
            {user ? (
              <li>
                <Link
                  to="/account"
                  className="nav-link account-btn"
                  onClick={handleAccountClick}
                >
                  <i className="fas fa-user-circle"></i>
                  Account
                </Link>
              </li>
            ) : (
              <li>
                <button
                  className="nav-link header-register-btn"
                  onClick={() => {
                    setShowLogin(true);
                    setIsMenuOpen(false);
                  }}
                >
                  Register
                </button>
              </li>
            )}
          </ul>
          <div
            className={`hamburger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/hackathon" element={<HackathonPage />} />
        <Route path="/workshop" element={<WorkshopPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/eventtimeline" element={<EventTimelinePage />} />
        <Route path="/coordinators" element={<CoordinatorsPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/sponsors" element={<SponsorsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>

      {showLogin && <LoginRegister onClose={() => setShowLogin(false)} />}
    </div>
  );
}

export default App;
